import { useQuery } from '@tanstack/react-query';
import { Phone, TimerReset } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { fetchAdvisorTrials } from '@/data/billing';

const STATE_LABEL: Record<string, string> = {
  active: 'Free trial',
  ending_soon: 'Trial ending soon',
  expired: 'Trial ended',
};

const STATE_STYLE: Record<string, string> = {
  expired: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  ending_soon: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  active: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
};

export default function ClientTrialBlock({ clientId }: { clientId: string }) {
  const { data } = useQuery({
    queryKey: ['advisor-trials'],
    queryFn: fetchAdvisorTrials,
    retry: false,
  });

  const row = (data ?? []).find((entry) => entry.advisor_client_id === clientId);
  if (!row || row.plan !== 'trial' || !row.trial_state) return null;

  return (
    <Card className="border-amber-500/30 bg-amber-500/5" data-testid="client-trial-block">
      <CardContent className="p-4 flex flex-wrap items-center gap-3">
        <TimerReset className="h-4 w-4 text-amber-500 shrink-0" />
        <Badge variant="outline" className={STATE_STYLE[row.trial_state] ?? ''}>
          {STATE_LABEL[row.trial_state]}
        </Badge>
        <span className="text-sm" data-testid="client-trial-summary">
          {row.trial_state === 'expired'
            ? `${row.name} is out of trial and not yet on a paid plan.`
            : `${row.days_left} day${row.days_left === 1 ? '' : 's'} left${
                row.trial_ends_at
                  ? `, until ${new Date(row.trial_ends_at).toLocaleDateString()}`
                  : ''
              }.`}
        </span>
        {row.contact_phone && (
          <a
            href={`tel:${row.contact_phone.replace(/\s+/g, '')}`}
            className="inline-flex items-center gap-1 text-sm underline ml-auto"
            data-testid="client-trial-phone"
          >
            <Phone className="h-3 w-3" />
            {row.contact_phone}
          </a>
        )}
      </CardContent>
    </Card>
  );
}
